import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Filler
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";

/* ================= REGISTER ================= */
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

/* ================= COMPONENT ================= */
const MonthlySalesBarLineChart = ({ data = [], height = 420 }) => {
  /* ================= RESPONSIVE ================= */
  const isMobile = window.innerWidth < 640;

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        No Data
      </div>
    );
  }

 const formatIndiann = (num) => {
  const n = Number(num || 0);
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);

  if (abs >= 10000000) return `${sign}${(abs / 10000000).toFixed(2)} Cr`;
  if (abs >= 100000) return `${sign}${(abs / 100000).toFixed(2)} L`;
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(2)} K`;
  return `${sign}${abs}`;
};

const formatIndian = (num) => {
    const n = Number(num || 0);
    const sign = n < 0 ? "-" : "";
    const abs = Math.abs(n);

    return `${sign}${(abs / 100000).toFixed(2)} L`;
};

  const formatMonth = (monthValue) => {
    if (!monthValue) return "";
    if (/^[A-Za-z]{3}-\d{4}$/.test(String(monthValue))) return monthValue;

    const dt = new Date(monthValue);
    if (Number.isNaN(dt.getTime())) return String(monthValue);

    const mon = dt.toLocaleString("en-US", { month: "short" });
    const yr = dt.getFullYear();
    return `${mon}-${yr}`;
  };

  /* ================= LABELS ================= */
  const labels = data.map((d) => formatMonth(d.month));

  /* ================= SERIES ================= */
  const sales = data.map(d => Number(d.sales || 0));
  const returns = data.map(d => Number(d.returns || 0));

  /* ================= SCROLL WIDTH ================= */
  const pointWidth = isMobile ? 90 : 100;
  const chartWidth = isMobile
    ? Math.max(labels.length * pointWidth, window.innerWidth)
    : "100%";

  /* ================= CHART DATA ================= */
  const chartData = {
    labels,
    datasets: [
      {
        label: "Sales",
        data: sales,
        borderWidth: 1,
        borderColor: "#2e7d32",
        backgroundColor: "rgba(76, 175, 80, 0.75)",
        borderRadius: 6,
        barThickness: isMobile ? 18 : 24,
        maxBarThickness: 28,
        datalabels: {
          color: "#2e7d32"
        }
      },
      {
        label: "Returns",
        data: returns,
        borderWidth: 1,
        borderColor: "#c62828",
        backgroundColor: "rgba(229, 57, 53, 0.75)",
        borderRadius: 6,
        barThickness: isMobile ? 18 : 24,
        maxBarThickness: 28,
        datalabels: {
          color: "#e53935"
        }
      }
    ]
  };

  /* ================= OPTIONS ================= */
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 6,
        right: 6,
        top: 0,
        bottom: 0
      }
    },

    animation: {
      duration: 2000,
      easing: "easeOutQuart"
    },

    plugins: {
      legend: {
        position: "top",
        align: "end"
      },

      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          footer: items => {
            const i = items[0].dataIndex;
            const total = sales[i] + returns[i];
            return `Total: ${formatIndian(total)}`;
          },
          label: (context) => {
            const value = Number(context.raw || 0);
            return `${context.dataset.label}: ${formatIndian(value)}`;
          }
        }
      },

      datalabels: {
        display: true,
        align: "top",
        anchor: "end",
        offset: 0,
        clamp: true,
        clip: false,
        formatter: (v) => formatIndian(v),
        font: {
          weight: "800",
          size: 8
        },
        color: "#333"
      }
    },

    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: isMobile ? 0 : 60,
          minRotation: isMobile ? 0 : 60,
          padding: 4
        },
        grid: { display: false }
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (v) => formatIndian(v)
        },
        title: {
          display: true,
          text: "Sale (Lakh)"
        }
      }
    }
  };

  /* ================= RENDER ================= */
  return (
    <div style={{ position: "relative" }}>
      {/* SCROLL HINT */}
      {isMobile && (
        <div
          style={{
            fontSize: 11,
            color: "#6b6b8a",
            textAlign: "right",
            marginBottom: 4
          }}
        >
          ← Swipe →
        </div>
      )}

      {/* SCROLLABLE CHART */}
      <div
        style={{
          height,
          position: "relative",
          overflowX: isMobile ? "auto" : "hidden",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch"
        }}
      >
        <div style={{ width: chartWidth, height: "100%" }}>
          <Bar data={chartData} options={options} />
        </div>

        {/* RIGHT FADE INDICATOR */}
        {/* {isMobile && (
          <div
            style={{
              pointerEvents: "none",
              position: "absolute",
              top: 0,
              right: 0,
              width: 36,
              height: "100%",
              background:
                "linear-gradient(to left, rgba(255,255,255,0.95), rgba(255,255,255,0))"
            }}
          />
        )} */}
      </div>
    </div>
  );
};

export default MonthlySalesBarLineChart;
